import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { publicCaptchaConfig } from '../lib/marketingApi';

function loadScript(src, id) {
  return new Promise((res, rej) => {
    if (document.getElementById(id)) { res(); return; }
    const s = document.createElement('script');
    s.src = src; s.id = id; s.async = true; s.defer = true;
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}

// Anti-spam widget supporting Google reCAPTCHA v2 (checkbox), reCAPTCHA v3
// (invisible/score) and Cloudflare Turnstile. Reports enabled state via
// onEnabledChange; parent obtains a token at submit time via ref.getToken().
const CaptchaWidget = forwardRef(function CaptchaWidget({ onEnabledChange, className = 'mt-4' }, ref) {
  const [cap, setCap] = useState(null);
  const boxRef = useRef(null);
  const widgetId = useRef(null);
  const tokenRef = useRef(''); // v2 / turnstile checkbox token

  const isV3 = cap?.provider === 'recaptcha' && cap?.version === 'v3';

  useEffect(() => {
    let alive = true;
    publicCaptchaConfig().then((c) => {
      if (!alive) return;
      setCap(c);
      if (onEnabledChange) onEnabledChange(!!c?.enabled);
    }).catch(() => {});
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cap?.enabled) return undefined;
    let cancelled = false;
    const provider = cap.provider;
    let src;
    if (provider === 'turnstile') src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    else if (isV3) src = `https://www.google.com/recaptcha/api.js?render=${cap.site_key}`;
    else src = 'https://www.google.com/recaptcha/api.js?render=explicit';

    loadScript(src, `captcha-${provider}-${isV3 ? 'v3' : 'v2'}`).then(() => {
      if (isV3) return; // v3 is invisible — nothing to render, token fetched on submit
      const render = () => {
        if (cancelled || !boxRef.current) return;
        const w = provider === 'turnstile' ? window.turnstile : (window.grecaptcha && window.grecaptcha.render ? window.grecaptcha : null);
        if (w) {
          boxRef.current.innerHTML = '';
          widgetId.current = w.render(boxRef.current, {
            sitekey: cap.site_key,
            callback: (t) => { tokenRef.current = t; },
            'expired-callback': () => { tokenRef.current = ''; },
          });
        } else { setTimeout(render, 300); }
      };
      render();
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [cap]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    async getToken() {
      if (!cap?.enabled) return '';
      if (isV3) {
        try {
          await new Promise((res) => window.grecaptcha.ready(res));
          return await window.grecaptcha.execute(cap.site_key, { action: 'submit' });
        } catch (e) { return ''; }
      }
      return tokenRef.current || '';
    },
    reset() {
      if (isV3) return;
      try {
        const w = cap?.provider === 'turnstile' ? window.turnstile : window.grecaptcha;
        if (w && widgetId.current != null) w.reset(widgetId.current);
      } catch (e) { /* noop */ }
      tokenRef.current = '';
    },
  }));

  // v3 shows no inline widget (Google adds a floating badge); v2/turnstile need a mount point.
  if (!cap?.enabled || isV3) return null;
  return <div ref={boxRef} data-testid="captcha-widget" className={className} />;
});

export default CaptchaWidget;
